import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';
import { Landmark } from 'lucide-react';
import { createElement } from 'react';
import type { MenuItem } from '@verclam/ui-kit';

type Args = {
	readonly organizations: readonly ComposedOrganization[];
	readonly selectFacilityEntry: (facilityId: number, organizationId: number) => void;
	readonly selectEmptyOrganizationEntry: (organizationId: number) => void;
};

/**
 * Builds the `Menu` model. A single organization that has facilities yields a flat facility list.
 * Otherwise each organization is one block, in the received order: a group of its facilities, or a
 * top-level clickable entry when it has none. A separator sits between two consecutive blocks only.
 */
const buildSelectorModel = ({
	organizations,
	selectFacilityEntry,
	selectEmptyOrganizationEntry,
}: Args): readonly MenuItem[] => {
	const [soleOrganization] = organizations;

	if (
		organizations.length === 1 &&
		soleOrganization !== undefined &&
		soleOrganization.facilities.length > 0
	) {
		return soleOrganization.facilities.map((facility) => ({
			label: facility.name,
			command: () => selectFacilityEntry(facility.id, soleOrganization.id),
		}));
	}

	const blocks: MenuItem[] = organizations.map((organization) => {
		if (organization.facilities.length === 0) {
			return {
				label: `${organization.name} · aucune agence`,
				icon: createElement(Landmark, { size: 16 }),
				command: () => selectEmptyOrganizationEntry(organization.id),
			};
		}

		return {
			label: organization.name,
			items: organization.facilities.map((facility) => ({
				label: facility.name,
				command: () => selectFacilityEntry(facility.id, organization.id),
			})),
		};
	});

	return blocks.flatMap((block, index) => (index === 0 ? [block] : [{ separator: true }, block]));
};

export default buildSelectorModel;
